/**
 * useHeaderReveal — hide the header after a run of downward gesture pixels, bring it back on the
 * first run of upward ones, always show it at the top, and hold it shown solid while focus is
 * inside it or the wall overlay is open. Plan `thinking/plans/2026_09_04_wall_header_reveal.md` §
 * Step 1; board `thinking/design/toile_wall/HeaderReveal.dc.html`.
 *
 * Lives in `core/ui`, not `core/wall/ui`: the header is a product component (Toile's today, Narcis's
 * and Ovid's in plans f/g, § Step 4), so this hook must not import from `@core/wall` — the contract
 * it needs from the wall (`data-wall-overlay` on `<html>`) is read off the DOM, never imported.
 *
 * GESTURE-DRIVEN, never `scrollY`-DERIVED (the rule the wall's own scroll-return listener follows
 * for the identical reason, `core/wall/ui/Wall.tsx`'s own comment on its `wheel`/`touchmove`
 * effect): a programmatic scroll (`window.scrollTo`, the placement plan's Continue ↑, a back-
 * navigation restore, a focus-scroll) moves `scrollY` with no wheel, touch or scroll-key behind it,
 * and reading that delta as the user's own downward read would hide the header the instant the page
 * arrives already scrolled. `atTop` is the one piece of state this hook DOES read off `scrollY`
 * directly (BRIEF: "always shown at the top" cares about POSITION, not gesture direction) — `hidden`
 * never does.
 *
 * TEST NOTE — deviation from the plan's own `jsdom` ask, on the repo's own established precedent
 * (`core/consent/useConsentGate.ts`'s header, `core/hooks/__tests__/useModelIntake.spec.ts`): this
 * project runs `testEnvironment: 'node'` (`jest.config.js`) with no jsdom and no
 * `@testing-library/react` in `package.json` (grep confirms neither, 2026-09-04), and a worktree
 * besides must not `npm install` to add either (`webapp/CLAUDE.md`: the `@tensorflow/tfjs-node`
 * ARM64 caveat makes a fresh install in a worktree unsafe, and it would edit the shared
 * `package.json`/lockfile another session on this branch may also be touching). The DOM-independent
 * decision logic below — the threshold accumulator, the direction reset, the top gate, the
 * focus-inside transition, the overlay freeze, the post-overlay grace, the scroll-key mapping — is
 * exported as plain functions and carries real, executable coverage
 * (`__tests__/useHeaderReveal.spec.ts`). The hook's own React wiring (the listeners, the rAF
 * coalescing, `focusin`/`focusout`, the `MutationObserver` on `data-wall-overlay`) is exercised by
 * the dev-verify Chrome loop instead — the class application itself is the component's job either
 * way (`Header.tsx`), not this hook's, per the plan's own "reduced-motion class application is the
 * component's."
 */
import {useEffect, useRef, useState, type RefObject} from 'react'

/** Net downward pixels (wheel `deltaY`, inverted touch delta, or a scroll-key's own pixel stand-in)
 *  accumulated since the last direction reversal, top arrival, pin, or overlay-close grace expiry
 *  before the header hides. The asymmetry against `SHOW_THRESHOLD` absorbs trackpad jitter at the
 *  tail of inertia (edge case 1). */
export const HIDE_THRESHOLD = 24
/** Net upward pixels before the header returns. */
export const SHOW_THRESHOLD = 8
/** How long after the wall's overlay (`data-wall-overlay`) closes its own momentum is still ignored
 *  (edge case 5: "the fold's own momentum must not hide the header the instant the wall is back"). */
export const OVERLAY_GRACE_MS = 250

export type RevealState = {
    hidden: boolean
    atTop: boolean
    /** Signed net accumulation since the last reset. Positive = downward. Exposed only for the
     *  pure-function tests below; the hook itself never reads it from outside `applyGestureDelta`. */
    accum: number
}

export function initialRevealState(): RevealState {
    return {hidden: false, atTop: true, accum: 0}
}

/**
 * One gesture delta, positive downward. A reversal (the new delta's sign disagreeing with the
 * running accumulation) resets the accumulator TO the new delta rather than to zero (plan: "a
 * direction change resets the accumulator") — a single decisive reversal counts immediately rather
 * than needing to first cancel out the old run.
 */
export function applyGestureDelta(state: RevealState, dy: number): RevealState {
    if (dy === 0) return state
    const sameDirection = state.accum === 0 || Math.sign(state.accum) === Math.sign(dy)
    const accum = sameDirection ? state.accum + dy : dy
    if (accum >= HIDE_THRESHOLD) return {...state, accum, hidden: true}
    if (accum <= -SHOW_THRESHOLD) return {...state, accum, hidden: false}
    return {...state, accum}
}

/** `window.scrollY <= headerHeight` (iOS rubber-band's negative `scrollY` counts as top too, since
 *  the caller never passes a negative value through as "not top"). Arriving at the top always shows
 *  the header and clears whatever was accumulating, so the next downward run starts fresh from
 *  zero rather than from wherever it last stood. */
export function applyAtTop(state: RevealState, atTop: boolean): RevealState {
    if (!atTop) return state.atTop ? {...state, atTop: false} : state
    if (state.atTop && !state.hidden && state.accum === 0) return state
    return {...state, atTop: true, hidden: false, accum: 0}
}

/** A focus gain/loss resolves here too (`applyFocusMove` below): shown, accumulator cleared, so the
 *  header does not immediately re-hide from whatever had been accumulating before focus entered
 *  it. */
export function resetToShown(state: RevealState): RevealState {
    return state.hidden || state.accum !== 0 ? {...state, hidden: false, accum: 0} : state
}

/**
 * Whether focus is inside the header, after one `focusin`/`focusout` transition — the invariant
 * code-quality review F7 found untested (the incident F6/F7 both name, `51b459128`): one pin per
 * ENTRY into the header, one unpin per EXIT, so a focus move WITHIN it (the search field to the
 * avatar, the avatar to a menu item — which fires a `focusout` immediately followed by a `focusin`,
 * both with the OTHER end inside the header) must leave the answer unchanged rather than growing a
 * counter that never gives back what it took. `relatedInside` is the event's own `relatedTarget`
 * tested against the header root; `entering` is which of the two events this is.
 */
export function applyFocusMove(inside: boolean, args: {entering: boolean; relatedInside: boolean}): boolean {
    if (args.relatedInside) return inside // a move within the header — the answer does not change
    return args.entering
}

/** Whether a gesture should be dropped entirely rather than reaching `applyGestureDelta` — the
 *  wall's overlay is open, or it closed inside the last `OVERLAY_GRACE_MS` (edge case 5). Whether
 *  focus is inside the header is checked separately by the hook itself, off the same boolean
 *  `applyFocusMove` computes (a plain ref read, not worth a pure function of its own). */
export function shouldIgnoreGesture(args: {overlayOpen: boolean; graceUntil: number | null; now: number}): boolean {
    if (args.overlayOpen) return true
    return args.graceUntil !== null && args.now < args.graceUntil
}

/** `ArrowDown`/`ArrowUp`, `PageDown`/`PageUp`, `Space` (with and without Shift), `Home`/`End` — the
 *  scroll keys the hook listens for (plan § Step 1, edge case 2) — mapped to a signed pixel stand-in
 *  large enough to cross the relevant threshold in one press, matching roughly what each key already
 *  scrolls the page by. `Home`/`End` map to a value that always wins regardless of anything already
 *  accumulated, since they jump to an end rather than nudge a direction. Everything else is `null`
 *  (not a scroll key, or unhandled) — the caller's own edit-target check runs first. */
export function keyGestureDelta(key: string, shiftKey: boolean): number | null {
    switch (key) {
        case 'ArrowDown':
            return 40
        case 'ArrowUp':
            return -40
        case 'PageDown':
            return 120
        case 'PageUp':
            return -120
        case ' ':
        case 'Spacebar':
            return shiftKey ? -120 : 120
        case 'Home':
            return -9999
        case 'End':
            return 9999
        default:
            return null
    }
}

function isEditableTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false
    return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable
}

function overlayIsOpen(): boolean {
    return document.documentElement.getAttribute('data-wall-overlay') === 'open'
}

export type HeaderRevealResult = {
    /** Apply as `translateY(-100%)` — already `false` while focus is inside the header or at the
     *  top, so the caller need not re-check either. */
    hidden: boolean
    /** Drives the floating shadow: none at the top, the board's soft shadow once scrolled. */
    atTop: boolean
}

export function useHeaderReveal(headerRef: RefObject<HTMLElement | null>): HeaderRevealResult {
    const [state, setState] = useState<RevealState>(initialRevealState)
    const headerHeightRef = useRef(0)
    /** Whether focus is currently inside the header (`applyFocusMove`'s own state) — the header's
     *  ONLY reason to hold itself shown against a gesture, so a boolean is the whole answer
     *  (code-quality review F6: the ref-counted `pin`/`unpin` API this replaced had exactly one
     *  internal producer — this listener pair — so the count could only ever be 0 or 1, and the
     *  counting was the whole surface the 09-07 leak lived in, not any of its value). No external
     *  caller ever destructured `pin`/`unpin` off this hook's result (a repo-wide grep at the time
     *  of the review found none), so both are dropped from `HeaderRevealResult` until a second
     *  reason to hold the header shown actually exists. */
    const insideHeaderRef = useRef(false)
    const overlayGraceUntilRef = useRef<number | null>(null)
    const rafRef = useRef<number | null>(null)
    const pendingDeltaRef = useRef(0)

    // Measure the header's own height (the `atTop` threshold) on mount and on resize/orientation
    // change (plan: "Resize / orientation. Re-measure the header height; recompute atTop; keep the
    // state.").
    useEffect(() => {
        function measure() {
            headerHeightRef.current = headerRef.current?.getBoundingClientRect().height ?? 0
        }
        measure()
        window.addEventListener('resize', measure)
        return () => window.removeEventListener('resize', measure)
    }, [headerRef])

    // `atTop` tracking — maintained by the scroll listener ALONE, never `hidden` (see the header
    // doc's gesture-vs-scrollY split). Passive; a very tall/short page (edge case 17: privacy,
    // terms) just never crosses the threshold, so nothing here special-cases document height.
    useEffect(() => {
        function onScroll() {
            const y = window.scrollY
            const atTop = y <= headerHeightRef.current
            setState((prev) => applyAtTop(prev, atTop))
        }
        onScroll()
        window.addEventListener('scroll', onScroll, {passive: true})
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    // The overlay freeze — `data-wall-overlay` lives on `<html>`, set by the wall (`Wall.tsx`), read
    // here rather than imported (this hook must not depend on `@core/wall`). A `MutationObserver`
    // rather than polling: the attribute changes exactly when the wall's own `overlayOpen` (or its
    // sign-in/consent siblings) does, so this is a push, not a guess.
    useEffect(() => {
        let wasOpen = overlayIsOpen()
        const observer = new MutationObserver(() => {
            const open = overlayIsOpen()
            if (wasOpen && !open) overlayGraceUntilRef.current = Date.now() + OVERLAY_GRACE_MS
            wasOpen = open
        })
        observer.observe(document.documentElement, {attributes: true, attributeFilter: ['data-wall-overlay']})
        return () => observer.disconnect()
    }, [])

    // Focus anywhere inside the header holds it shown (plan: "focus lands anywhere inside the
    // header" — a keyboard user tabbing into a transformed-away header would otherwise focus an
    // invisible control; edge case 10). `focusin`/`focusout` bubble, unlike `focus`/`blur`, so one
    // listener on the header root covers the search field, the account menu's button and its items
    // alike — no separate wiring needed in `HeaderSearch` or `UserBlock` for this.
    //
    // `applyFocusMove` carries the invariant this incident broke, 2026-09-07: the old `focusin`
    // pinned on every focus gain while `focusout` unpinned only on leaving, so a focus move WITHIN
    // the header (search field to avatar, avatar to a menu item) grew a ref-counted pin by one it
    // never gave back, and the header stayed shown for the rest of the page's life — "does not hide
    // on scroll down", seen most on Android (Chrome focuses tapped links and buttons; iOS Safari
    // does not, which is why it only sometimes reproduced there). Fixed at F7's own root: the
    // decision is now a pure function under test, and the listeners below only classify the event
    // and apply what it returns.
    useEffect(() => {
        const el = headerRef.current
        if (!el) return
        function inside(node: EventTarget | null): boolean {
            return node instanceof Node && !!el?.contains(node)
        }
        function onFocusIn(e: FocusEvent) {
            const next = applyFocusMove(insideHeaderRef.current, {entering: true, relatedInside: inside(e.relatedTarget)})
            if (next === insideHeaderRef.current) return
            insideHeaderRef.current = next
            setState((prev) => resetToShown(prev))
        }
        function onFocusOut(e: FocusEvent) {
            const next = applyFocusMove(insideHeaderRef.current, {entering: false, relatedInside: inside(e.relatedTarget)})
            if (next === insideHeaderRef.current) return
            // Deferred one frame (edge case 11): a menu's own outside-click closing it must not
            // hide the header inside that same event — the ref itself flips at once, so the gesture
            // handlers below (which read it synchronously) drop any gesture landing in the same
            // tick; only the visible reset waits a frame.
            insideHeaderRef.current = next
            requestAnimationFrame(() => setState((prev) => resetToShown(prev)))
        }
        el.addEventListener('focusin', onFocusIn)
        el.addEventListener('focusout', onFocusOut)
        return () => {
            el.removeEventListener('focusin', onFocusIn)
            el.removeEventListener('focusout', onFocusOut)
        }
    }, [headerRef])

    // The gestures themselves — wheel, touchmove, keydown. One effect, attached once: the
    // focus-inside/overlay state is read live off the refs/DOM inside the handlers rather than
    // captured in the dependency array, so the listeners never need to be torn down and re-attached
    // as those change.
    useEffect(() => {
        function queue(dy: number) {
            if (insideHeaderRef.current) return
            if (shouldIgnoreGesture({overlayOpen: overlayIsOpen(), graceUntil: overlayGraceUntilRef.current, now: Date.now()})) return
            pendingDeltaRef.current += dy
            if (rafRef.current === null) {
                rafRef.current = requestAnimationFrame(() => {
                    rafRef.current = null
                    const dy2 = pendingDeltaRef.current
                    pendingDeltaRef.current = 0
                    if (dy2 === 0) return
                    setState((prev) => applyGestureDelta(prev, dy2))
                })
            }
        }

        function onWheel(e: WheelEvent) {
            queue(e.deltaY)
        }

        let lastTouchY: number | null = null
        function onTouchStart(e: TouchEvent) {
            lastTouchY = e.touches[0]?.clientY ?? null
        }
        function onTouchMove(e: TouchEvent) {
            const y = e.touches[0]?.clientY
            if (y === undefined) return
            if (lastTouchY === null) {
                lastTouchY = y
                return
            }
            // Sign inverted (plan): the finger moving UP the screen (y decreasing) is the same
            // "scroll down" direction as a positive wheel `deltaY`.
            queue(lastTouchY - y)
            lastTouchY = y
        }
        function onTouchEnd() {
            lastTouchY = null
        }

        function onKeyDown(e: KeyboardEvent) {
            if (isEditableTarget(e.target)) return
            const dy = keyGestureDelta(e.key, e.shiftKey)
            if (dy !== null) queue(dy)
        }

        document.addEventListener('wheel', onWheel, {passive: true})
        document.addEventListener('touchstart', onTouchStart, {passive: true})
        document.addEventListener('touchmove', onTouchMove, {passive: true})
        document.addEventListener('touchend', onTouchEnd, {passive: true})
        // keydown is the one non-passive listener (plan § Step 1's own "all listeners passive except
        // keydown") — it needs to read `e.key`/`e.shiftKey`/`e.target` synchronously, never calls
        // `preventDefault`, so it does not block the browser's own key-scroll from also happening.
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('wheel', onWheel)
            document.removeEventListener('touchstart', onTouchStart)
            document.removeEventListener('touchmove', onTouchMove)
            document.removeEventListener('touchend', onTouchEnd)
            document.removeEventListener('keydown', onKeyDown)
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
        }
    }, [])

    return {hidden: state.hidden, atTop: state.atTop}
}
